import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ClockIcon, FlameIcon, ZapIcon, UserIcon, LayersIcon, ShieldCheckIcon, ArrowRightIcon } from 'lucide-react';
export function InnerIntentionsPage() {
  const gameModes = [{
    title: 'Speed Dating',
    desc: 'Fast-paced dating compatibility',
    icon: <ZapIcon className="w-6 h-6" data-id="element-745" />,
    color: 'border-pink-500 text-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.3)]'
  }, {
    title: 'Hot Seat',
    desc: 'Main solo card deck for relationship/spicy',
    icon: <FlameIcon className="w-6 h-6" data-id="element-746" />,
    color: 'border-orange-500 text-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.3)]'
  }, {
    title: 'Chaos Mode',
    desc: 'Funny + messy party vibes',
    icon: <LayersIcon className="w-6 h-6" data-id="element-747" />,
    color: 'border-cyan-400 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.3)]'
  }, {
    title: 'Solo Mode',
    desc: 'Personal insight decks',
    icon: <UserIcon className="w-6 h-6" data-id="element-748" />,
    color: 'border-blue-400 text-blue-400 shadow-[0_0_15px_rgba(96,165,250,0.3)]'
  }];
  return <div className="min-h-screen bg-[#0a0014] text-white pt-28 pb-24 overflow-hidden relative" data-id="element-749">
      {/* Dark Neon Background Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-fuchsia-600/20 rounded-full blur-[120px] pointer-events-none" data-id="element-750" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-600/20 rounded-full blur-[120px] pointer-events-none" data-id="element-751" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[150px] pointer-events-none" data-id="element-752" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-753">
        {/* Hero Image / Screenshot */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full rounded-3xl overflow-hidden border border-fuchsia-500/30 shadow-[0_0_30px_rgba(219,39,119,0.2)] mb-12 relative" data-id="element-754">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0014] via-transparent to-transparent z-10" data-id="element-755" />
          <img src="https://cdn.magicpatterns.com/uploads/huZ3hXBRRYknnb7NhUyad3/Screenshot_2026-05-12_141014.png" alt="Inner Intentions App Interface" className="w-full h-auto object-cover" data-id="element-756" />
        </motion.div>

        {/* Header & Logo */}
        <div className="text-center mb-16 relative z-20 -mt-24" data-id="element-757">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} className="w-32 h-32 mx-auto mb-6 rounded-3xl bg-black/80 backdrop-blur-md flex items-center justify-center p-4 border border-fuchsia-500/50 shadow-[0_0_20px_rgba(236,72,153,0.4)]" data-id="element-758">
            <img src="https://cdn.magicpatterns.com/uploads/hJ4WPpi2ZC6Jbgi4km7R7P/ChatGPT_Image_May_11,_2026,_06_59_04_PM.png" alt="Inner Intentions Logo" className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(236,72,153,0.8)]" data-id="element-759" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="font-heading text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-fuchsia-400 to-purple-400 mb-4 drop-shadow-[0_0_10px_rgba(236,72,153,0.5)]" data-id="element-760">
            Inner Intentions
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-xl text-fuchsia-200 font-medium tracking-widest uppercase mb-8" data-id="element-761">
            We grow here. No filters. Just real.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-fuchsia-600/30 text-fuchsia-200 font-bold text-lg border border-fuchsia-500/40 shadow-[0_0_20px_rgba(219,39,119,0.3)] cursor-default" data-id="element-762">
            <ClockIcon className="w-5 h-5" data-id="element-763" />
            Coming Soon
          </motion.div>
          <p className="text-fuchsia-400/60 text-sm mt-3" data-id="element-764">
            Launching soon on App Store & Google Play
          </p>
        </div>

        {/* Description */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-fuchsia-950/30 backdrop-blur-sm rounded-3xl p-8 md:p-10 border border-fuchsia-500/20 mb-16" data-id="element-765">
          <p className="text-fuchsia-100 text-lg leading-relaxed mb-6" data-id="element-766">
            Inner Intentions is a social conversation and self-discovery game
            designed to spark real connections, meaningful conversations, and
            unforgettable moments. Whether you're playing with friends, a date,
            a partner, or even solo, the game uses personality-driven question
            decks, compatibility insights, chaos rounds, hot seat challenges,
            and reflective prompts.
          </p>
          <p className="text-fuchsia-100 text-lg leading-relaxed" data-id="element-767">
            From lighthearted chaos rounds to vulnerable deeper-connection
            questions, Inner Intentions is built for real conversations, real
            personalities, and genuine human connection.
          </p>
        </motion.div>

        {/* Game Modes Grid */}
        <div className="mb-20" data-id="element-768">
          <h2 className="font-heading text-3xl font-bold text-center text-white mb-10 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]" data-id="element-769">
            Choose Your Vibe
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-770">
            {gameModes.map((mode, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className={`bg-black/60 backdrop-blur-md p-6 rounded-2xl border ${mode.color} flex flex-col items-center text-center hover:-translate-y-1 transition-transform`} data-id="element-771">
                <div className="mb-4 p-3 rounded-full bg-white/5" data-id="element-772">
                  {mode.icon}
                </div>
                <h3 className="font-bold text-lg mb-2 text-white" data-id="element-773">
                  {mode.title}
                </h3>
                <p className="text-sm opacity-80" data-id="element-774">{mode.desc}</p>
              </motion.div>)}
          </div>
        </div>

        {/* What You'll Discover */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-20" data-id="element-775">
          <h2 className="font-heading text-2xl font-bold text-fuchsia-300 mb-6" data-id="element-776">
            What You'll Discover
          </h2>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-777">
            {['Communication Styles', 'Emotional Patterns', 'Green Flags', 'Red Flags', 'Chemistry', 'Humor', 'Personal Growth', 'Dating Habits', 'Boundaries'].map((tag, i) => <span key={i} className="px-4 py-2 rounded-full bg-fuchsia-900/40 border border-fuchsia-500/30 text-fuchsia-200 text-sm font-medium" data-id="element-778">
                {tag}
              </span>)}
          </div>
        </motion.div>

        {/* Privacy Policy Link Card */}
        <Link to="/apps/inner-intentions/privacy" className="group block bg-black/40 backdrop-blur-md rounded-3xl p-6 md:p-8 border border-fuchsia-900/50 hover:border-fuchsia-500/60 hover:shadow-[0_0_30px_rgba(219,39,119,0.25)] transition-all" data-id="element-779">
          <div className="flex items-center gap-5" data-id="element-780">
            <div className="w-14 h-14 rounded-2xl bg-fuchsia-950/50 border border-fuchsia-500/30 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(236,72,153,0.3)] group-hover:shadow-[0_0_20px_rgba(236,72,153,0.5)] transition-shadow" data-id="element-781">
              <ShieldCheckIcon className="w-7 h-7 text-fuchsia-300" data-id="element-782" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-783">
              <h3 className="font-heading text-xl font-bold text-white mb-1" data-id="element-784">
                Privacy Policy
              </h3>
              <p className="text-fuchsia-300/70 text-sm font-medium" data-id="element-785">
                How we handle your data — effective May 12, 2026
              </p>
            </div>
            <ArrowRightIcon className="w-5 h-5 text-fuchsia-400 group-hover:text-fuchsia-200 group-hover:translate-x-1 transition-all shrink-0" data-id="element-786" />
          </div>
        </Link>
      </div>
    </div>;
}